import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import instance from "../../redux/api";
import { workflowBankTransaction } from "../../Services/api";
import { commonToast } from "../common";
import { CommonButton } from "../common/commonButton";
import { DisplayCurrentStepDetails } from "./EditPriceDialog";

const CloseIconStyle = {
  backgroundColor: "#3B3F44",
  color: "#ffff",
  height: "16px",
  width: "16px",
  padding: "7px 8px",
  borderRadius: "32px",
};

const customInputStyle = {
  fontWeight: "bold",
  fontSize: "14px",
  backgroundColor: "#f5f5f5",
  borderRadius: "8px",
};

const labelStyle = { minWidth: 180, fontWeight: "bold", fontSize: "15px" };

const ContraDetailsDialog = ({
  open,
  onClose,
  contraData,
  currentWorkflowDetails,
}) => {
  const { userData } = useSelector((state) => state?.user);

  const [loading, setLoading] = useState(false);

  // Extract fields as per workflow creation payload
  const cashData = contraData?.changefields?.contra_cash_allocation_details || {};
  const referencedPolicies = contraData?.changefields?.policyDetails || [];

  let referencedTotalReceivableAmount = 0;
  referencedPolicies.forEach(item => {
    referencedTotalReceivableAmount += item.receivable_amount || 0;
  });

  const bankTxnId = contraData?.bank_txn_id || "-";
  const comments = contraData?.changefields?.contraComment || contraData?.contra_cash_allocation_details?.comment || "";

  const [localComments, setLocalComments] = React.useState(comments || "");

  if (!contraData) return null;

  const handleInputChange = (fieldName, value) => {
    if (fieldName === "comments") setLocalComments(value);
  };

  const handleActionButton = async (buttonType) => {
    const { id, current_step } = currentWorkflowDetails;

    if (buttonType === "Cancel") {
      onClose();
      return;
    }

    const body = {
      step_name: current_step?.toLowerCase(),
      bank_txn_id: bankTxnId,
      comments: comments ?? "",
      status: buttonType,
      user_id: userData?.id,
    };

    try {
      setLoading(true);
      const response = await instance.patch(
        `${workflowBankTransaction}${id}/`,
        body
      );

      if (response && response?.status === 200 && buttonType?.toLowerCase() === "approve") {
        // Approval Handler
        commonToast("success", "Workflow Approved Successfully !");
        setLoading(false);
        onClose();
        window.location.reload();
      }
      else if (response && response?.status === 200 && buttonType?.toLowerCase() === "reject") {
        // Rejection Handler
        commonToast("success", "Workflow Rejected!");
        setLoading(false);
        onClose();
        window.location.reload();
      }
      else {
        throw new Error("Unable to Submit Workflow Action");
      }

    } catch (err) {
      console.log(err);
      commonToast("error", "Unable to Submit Workflow Action");
      setLoading(false);
    }
  };

  // Function to check if Approve/Reject should be shown
  const isWorkflowInProcessAndLoggedInUserExists = () => {
    const currentUserId = userData?.id;
    const { current_step, workflow_status } = currentWorkflowDetails || {};

    const getWorkflowSteps =
      currentWorkflowDetails?.workflow_json_data?.workflow_step;

    if (!getWorkflowSteps || !current_step) return false;
    const getCurrentStepDetails = getWorkflowSteps.filter(
      (item) => item?.step_name === current_step
    );

    if (workflow_status?.toLowerCase() === "in process") {
      if (getCurrentStepDetails?.length === 1) {
        const currentStepUsersList = getCurrentStepDetails[0]?.user;

        const getIndex = currentStepUsersList?.findIndex(
          (item) => item?.id === currentUserId
        );
        if (getIndex !== -1) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    loading ? (
      <CircularProgress
        style={{
          position: "absolute",
          top: "70%",
          left: "50%",
        }}
      />
    ) :
      <Dialog open={open} fullWidth>
        <DialogTitle id="contra-dialog-title" sx={{ cursor: "default", pb: 0 }}>
          {"Contra Allocation Request Flow"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon style={CloseIconStyle} />
          </IconButton>
          <hr style={{ width: "100%" }} />
        </DialogTitle>
        <DialogContent
          style={{
            height: "auto",
            width: "auto",
            padding: "10px 10px 6px 27px",
          }}
        >
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                paddingBottom: 0,
                minHeight: 0,
                "&.Mui-expanded": {
                  minHeight: 0,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", fontSize: "16px", color: "#000000" }}
              >
                Contra Allocation details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 0 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Policy ID</Typography>
                  <TextField
                    size="small"
                    value={cashData.policy_id || "-"}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Receivable Amount</Typography>
                  <TextField
                    size="small"
                    value={cashData.receivable_amt || 0}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Allocated Amount</Typography>
                  <TextField
                    size="small"
                    value={cashData.allocated_amt || 0}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Unallocated Amount</Typography>
                  <TextField
                    size="small"
                    value={cashData.unallocated_amt || 0}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>
                    Installment Amount (Org)
                  </Typography>
                  <TextField
                    size="small"
                    value={cashData.installment_amount_org || 0}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Bank Transaction ID</Typography>
                  <TextField
                    size="small"
                    value={bankTxnId}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    maxWidth: "50%",
                  }}
                >
                  <Typography sx={labelStyle}>Comments</Typography>
                  <textarea
                    rows={5}
                    style={{
                      height: "48px",
                      minWidth: "100px",
                      padding: "5px",
                      borderRadius: "5px",
                      resize: "vertical",
                      width: "100%",
                      boxSizing: "border-box",
                      backgroundColor: "#ffffff",
                    }}
                    onChange={(e) =>
                      handleInputChange("comments", e.target.value)
                    }
                    placeholder="Add reason"
                    disabled={true}
                    value={comments}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mb: 2 }}></Box>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                paddingBottom: 0,
                minHeight: 0,
                "&.Mui-expanded": {
                  minHeight: 0,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", fontSize: "16px", color: "#000000" }}
              >
                Referenced Policies
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 2 }}>
              <Box
                sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}
              >
                {Array.isArray(referencedPolicies) &&
                  referencedPolicies.length > 0 ? (
                  <TableContainer component={Paper} sx={{ maxWidth: 800 }}>
                    <Table size="medium">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              borderRight: "1px solid #e0e0e0",
                            }}
                          >
                            S. No.
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              borderRight: "1px solid #e0e0e0",
                            }}
                          >
                            Policy ID
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontSize: "14px", fontWeight: 600 }}
                          >
                            Receivable Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referencedPolicies.map((policy, idx) => (
                          <TableRow key={idx}>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "13px",
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              {idx + 1}
                            </TableCell>

                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "13px",
                                borderRight: "1px solid #e0e0e0",
                              }}
                            >
                              {policy.policy_id || "-"}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "13px" }}>
                              {policy.receivable_amount || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">No referenced policies.</Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mb: 2 }}></Box>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                paddingBottom: 0,
                minHeight: 0,
                "&.Mui-expanded": {
                  minHeight: 0,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", fontSize: "16px", color: "#000000" }}
              >
                Reconciliation
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 2 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Txn Receivable Amount</Typography>
                  <TextField
                    size="small"
                    value={Math.abs(cashData.receivable_amt)}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={labelStyle}>Referenced Total Receivable Amt</Typography>
                  <TextField
                    size="small"
                    value={referencedTotalReceivableAmount}
                    disabled={true}
                    InputProps={{ style: customInputStyle }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 250 }}>
                  <Typography sx={{ ...labelStyle, textAlign: "center" }}>Variance</Typography>
                  <TextField
                    size="small"
                    value={(Math.abs(cashData.receivable_amt) - referencedTotalReceivableAmount) || 0}
                    disabled
                    InputProps={{
                      style: { ...customInputStyle, textAlign: "center" }
                    }}
                    inputProps={{
                      style: { textAlign: "center" }
                    }}
                    fullWidth
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mb: 2 }}></Box>

          {Object.keys(currentWorkflowDetails)?.length > 0 && (
            <Box
              style={{
                backgroundColor: "#afabaa",
                margin: "20px 0px 0px 0px",
                padding: "20px",
                borderRadius: "5px",
              }}
            >
              <Typography style={{ color: "#000000" }}>
                Workflow Name :{" "}
                {currentWorkflowDetails?.workflow_json_data?.workflow_name}
              </Typography>
              <Typography style={{ color: "#000000" }}>
                Workflow Status : {currentWorkflowDetails?.workflow_status}
              </Typography>
              {currentWorkflowDetails?.current_step && (
                <Typography style={{ color: "#000000" }}>
                  Current Step : {currentWorkflowDetails?.current_step}
                </Typography>
              )}
              <br />
              <DisplayCurrentStepDetails
                workflowStepData={
                  currentWorkflowDetails?.workflow_json_data?.workflow_step
                }
                currentStep={currentWorkflowDetails?.current_step}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            text="Cancel"
            sx={{ 
              backgroundColor: "#1672DE !important",
              color: "white !important",
              borderRadius: 100,
              "&:hover": {
                backgroundColor: "#1565c0 !important",
              }
            }}
            hoverColor="#1565c0"
            handleClick={() => handleActionButton("Cancel")}
          />
          {isWorkflowInProcessAndLoggedInUserExists() && (
            <>
              <CommonButton
                text="Reject"
                sx={{ 
                  backgroundColor: "#1672DE !important",
                  color: "white !important",
                  borderRadius: 100,
                  "&:hover": {
                    backgroundColor: "#1565c0 !important",
                  }
                }}
                hoverColor="#1565c0"
                handleClick={() => handleActionButton("Reject")}
              />
              <CommonButton
                text="Approve"
                sx={{ 
                  backgroundColor: "#1672DE !important",
                  color: "white !important",
                  borderRadius: 100,
                  "&:hover": {
                    backgroundColor: "#1565c0 !important",
                  }
                }}
                hoverColor="#1565c0"
                handleClick={() => handleActionButton("Approve")}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
  );
};

export default ContraDetailsDialog;
